import i18n from '@/i18n';
import { stateSetter } from '@/utils';
import { IFspLight } from '../use-project-types/types';

export type InvitatieStatus =
	| 'trimisa'
	| 'acceptata'
	| 'refuzata'
	| 'expirata'
	| 'revocata';

export type InvitatieEventTip =
	| 'trimisa'
	| 'retrimisa'
	| 'acceptata'
	| 'refuzata'
	| 'expirata'
	| 'revocata';

type ChipColor =
	| 'default'
	| 'primary'
	| 'secondary'
	| 'error'
	| 'info'
	| 'success'
	| 'warning';

export interface InvitatieEvent {
	id: string;
	tip: InvitatieEventTip;
	at: string;
	byName: string;
	nota?: string;
}

export interface IInvitationAdministrationLevel {
	id: number;
	name: string;
	description?: string;
}

export interface IInvitationAdministration {
	id: number;
	name: string;
	code?: string;
	level?: IInvitationAdministrationLevel;
}

export interface IInvitationStatusInfo {
	id: number;
	name: string;
	uiStatus: InvitatieStatus;
}

export interface Invitatie {
	id: number | string;
	email: string;
	nume?: string;
	roleId?: number;
	roleName: string;
	administrations: IInvitationAdministration[];
	administrationName: string | null;
	statusId?: number;
	statusName?: string;
	status: InvitatieStatus;
	invitedByName: string;
	createdById?: number;
	// Utilizatorul care a acceptat invitația (prezent doar când statusul e „Acceptată").
	acceptedUserId?: number;
	acceptedUserName?: string;
	sentAt: string;
	expiresAt: string;
	updatedAt?: string;
	nrTrimiteri: number;
	istoric: InvitatieEvent[];
	token?: string;
}

export interface CreateInvitationPayload {
	email: string;
	roleId: number;
	roleName: string;
	// Listă: moderatorul poate avea mai multe administrații; single-admin exact una; platformă niciuna.
	administrationIds: number[];
	administrationName: string | null;
	invitedByName: string;
	// Județ + UAT declarate — OBLIGATORII pentru rolul de moderator, `null` pentru restul
	// (BE respinge county/uat pe roluri non-moderator).
	countyId: number | null;
	uatId: number | null;
}

export interface IInvitationsQuery {
	StatusId?: number;
	RoleId?: number;
	OrderBy?: string;
	Ascending?: boolean;
}

export const INVITATIE_STATUS_LABEL: Record<InvitatieStatus, string> = {
	trimisa: 'În așteptare',
	acceptata: 'Acceptată',
	refuzata: 'Refuzată',
	expirata: 'Expirată',
	revocata: 'Revocată'
};

// Cheile i18n ale statusurilor (editabile din Gestionare texte); fallback pe eticheta
// statică de mai sus.
const STATUS_LABEL_KEYS: Record<InvitatieStatus, string> = {
	trimisa: 'invitations.statusPending',
	acceptata: 'invitations.statusAccepted',
	refuzata: 'invitations.statusRejected',
	expirata: 'invitations.statusExpired',
	revocata: 'invitations.statusRevoked'
};
export const invitatieStatusLabel = (status: InvitatieStatus): string =>
	i18n.t(STATUS_LABEL_KEYS[status], { defaultValue: INVITATIE_STATUS_LABEL[status] });

export const INVITATIE_STATUS_COLOR: Record<InvitatieStatus, ChipColor> = {
	trimisa: 'warning',
	acceptata: 'success',
	refuzata: 'error',
	expirata: 'secondary',
	revocata: 'error'
};

export const INVITATIE_EVENT_LABEL: Record<InvitatieEventTip, string> = {
	trimisa: 'Invitație trimisă',
	retrimisa: 'Invitație retrimisă',
	acceptata: 'Invitație acceptată',
	refuzata: 'Invitație refuzată',
	expirata: 'Invitație expirată',
	revocata: 'Invitație revocată'
};

const EVENT_LABEL_KEYS: Record<InvitatieEventTip, string> = {
	trimisa: 'invitations.eventSent',
	retrimisa: 'invitations.eventResent',
	acceptata: 'invitations.eventAccepted',
	refuzata: 'invitations.eventRejected',
	expirata: 'invitations.eventExpired',
	revocata: 'invitations.eventRevoked'
};
export const invitatieEventLabel = (tip: InvitatieEventTip): string =>
	i18n.t(EVENT_LABEL_KEYS[tip], { defaultValue: INVITATIE_EVENT_LABEL[tip] });

export const INVITATIE_EVENT_COLOR: Record<InvitatieEventTip, ChipColor> = {
	trimisa: 'primary',
	retrimisa: 'warning',
	acceptata: 'success',
	refuzata: 'error',
	expirata: 'secondary',
	revocata: 'error'
};

export const normalizeInvitationStatus = (value?: string | null): InvitatieStatus => {
	const normalized = String(value || '')
		.trim()
		.toLowerCase();

	if (
		normalized.includes('accept') ||
		normalized === 'accepted' ||
		normalized === 'acceptata'
	) {
		return 'acceptata';
	}

	if (
		normalized.includes('refuz') ||
		normalized.includes('reject') ||
		normalized.includes('declin')
	) {
		return 'refuzata';
	}

	if (normalized.includes('expir')) {
		return 'expirata';
	}

	if (
		normalized.includes('revoc') ||
		normalized.includes('revoke') ||
		normalized.includes('cancel') ||
		normalized.includes('anulat')
	) {
		return 'revocata';
	}

	return 'trimisa';
};

export const effectiveStatus = (inv: Invitatie): InvitatieStatus => {
	if (inv.status === 'trimisa' && new Date(inv.expiresAt).getTime() < Date.now()) {
		return 'expirata';
	}

	return inv.status;
};

export const displayEvents = (inv: Invitatie): InvitatieEvent[] => {
	const base = [...(inv.istoric ?? [])];
	const hasExpired = base.some((event) => event.tip === 'expirata');

	if (
		!hasExpired &&
		inv.status === 'trimisa' &&
		new Date(inv.expiresAt).getTime() < Date.now()
	) {
		base.push({
			id: `syn-exp-${inv.id}`,
			tip: 'expirata',
			at: inv.expiresAt,
			byName: 'Sistem'
		});
	}

	return base.sort((a, b) => b.at.localeCompare(a.at));
};

export interface InvitationsReturnType<T extends Invitatie | Invitatie[]> {
	/** Config-ul de coloane din BE (api/invitations/table-config); null → coloanele locale. */
	tableConfig: import('@/components/table/column-config').IServerColumnConfig[] | null;
	loadingTableConfig: boolean;
	data: T | null;
	setData: stateSetter<T | null>;
	loading: boolean;
	loadingCTA: boolean;
	loadingCreate: boolean;
	administrations: IFspLight[];
	loadingAdministrations: boolean;
	statuses: IInvitationStatusInfo[];
	loadingStatuses: boolean;
	getInvitations: (
		queryParam?: IInvitationsQuery,
		pageParam?: number,
		itemsParam?: number
	) => Promise<void>;
	getInvitation: (invitationId: number | string) => Promise<void>;
	getAdministrations: () => Promise<void>;
	getStatuses: () => Promise<void>;
	createInvitation: (
		payload: CreateInvitationPayload,
		onSuccess?: (invitation: Invitatie) => void
	) => Promise<boolean>;
	resendInvitation: (
		invitationId: number | string,
		onSuccess?: (invitation: Invitatie) => void
	) => Promise<boolean>;
	revokeInvitation: (
		invitationId: number | string,
		onSuccess?: (invitation: Invitatie) => void
	) => Promise<boolean>;
	page: number;
	items: number;
	setPage: stateSetter<number>;
	setItems: stateSetter<number>;
	totalPages: number;
	totalItems: number;
}
